import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy, Eye, FileText, Globe, ImagePlus, Inbox, LogOut, MapPin, Newspaper, PenLine,
  QrCode, RefreshCw, Save, Send, Share2, Sparkles, TrendingUp, X,
} from "lucide-react";
import { brandApi, getBrandAuth, brandLogout } from "../lib/brandPortalApi";
import { errMsg } from "../lib/api";

const inputCls = "mt-1 w-full bg-white/[0.05] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60";

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4" data-testid={testid}>
      <Icon className="w-4 h-4 text-indigo-400" />
      <div className="text-2xl font-semibold text-white mt-2 tabular-nums">{value}</div>
      <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mt-0.5">{label}</div>
    </div>
  );
}

export default function BrandPortal() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [leads, setLeads] = useState([]);
  const [profile, setProfile] = useState({});
  const [topic, setTopic] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [shareArt, setShareArt] = useState(null);

  const load = async () => {
    try {
      const { data } = await brandApi.get("/brand/me");
      setMe(data);
      setProfile(data.brand);
      brandApi.get("/brand/leads").then(({ data: d }) => setLeads(d.leads || [])).catch(() => {});
    } catch {
      /* 401 interceptor redirects */
    }
  };

  useEffect(() => {
    if (!getBrandAuth()) { nav("/brand-login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveProfile = async () => {
    setSaveBusy(true);
    try {
      const fields = ["tagline", "description", "logo_url", "website", "instagram", "myntra_url", "meesho_url", "city", "keywords"];
      const body = Object.fromEntries(fields.map((f) => [f, profile[f] ?? ""]));
      await brandApi.put("/brand/me", body);
      toast.success("Profile updated — changes are live on your public page");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaveBusy(false);
    }
  };

  const generate = async () => {
    if (topic.trim().length < 3) { toast.error("Enter an article topic first"); return; }
    setGenBusy(true);
    try {
      await brandApi.post("/brand/articles/generate", { topic: topic.trim(), keywords: profile.keywords || "" });
      toast.success("AI article drafted! Review it below, then publish.");
      setTopic("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Generation failed — try again"));
    } finally {
      setGenBusy(false);
    }
  };

  const uploadImage = async (file, articleId = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (articleId) fd.append("article_id", articleId);
    const { data } = await brandApi.post("/brand/upload-image", fd);
    return data;
  };

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const { url } = await uploadImage(f);
      setProfile((p) => ({ ...p, logo_url: url }));
      toast.success("Logo uploaded — click Save Profile to apply");
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
  };

  const onArticleImage = async (e, articleId) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      await uploadImage(f, articleId);
      toast.success("Image added inside the article");
      load();
    } catch (err) { toast.error(errMsg(err, "Upload failed")); }
  };

  const setStatus = async (id, status) => {
    try {
      await brandApi.put(`/brand/articles/${id}`, { status });
      toast.success(status === "published" ? "Article is live!" : "Article unpublished");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!me) return <div className="min-h-screen bg-[#070A14] grid place-items-center text-slate-500 text-sm">Loading portal…</div>;

  const { brand, stats, articles } = me;

  return (
    <div className="min-h-screen bg-[#070A14] text-white">
      <header className="border-b border-white/10 px-5 sm:px-8 py-4 flex items-center justify-between sticky top-0 bg-[#070A14]/90 backdrop-blur-md z-10">
        <div className="flex items-center gap-3">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="w-9 h-9 rounded-lg object-cover bg-white" />
          ) : (
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 grid place-items-center font-bold">{brand.name[0]}</div>
          )}
          <div>
            <div className="font-semibold text-sm" data-testid="portal-brand-name">{brand.name}</div>
            <div className="text-[10px] text-indigo-400 uppercase tracking-wider font-bold">BrandsByIndia Portal</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href={stats.public_url} target="_blank" rel="noopener" data-testid="portal-view-public-page"
            className="text-xs px-3.5 py-2 rounded-full bg-white/[0.06] border border-white/10 hover:border-indigo-400/50 flex items-center gap-1.5 transition-colors">
            <Globe className="w-3.5 h-3.5 text-indigo-400" /> View Public Page
          </a>
          <button onClick={brandLogout} data-testid="portal-logout" className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Eye} label="Page Views" value={stats.page_views} testid="stat-page-views" />
          <StatCard icon={TrendingUp} label="Article Views" value={stats.article_views} testid="stat-article-views" />
          <StatCard icon={Newspaper} label="Articles Live" value={stats.articles_published} testid="stat-articles-live" />
          <StatCard icon={FileText} label="Total Articles" value={stats.articles_total} testid="stat-articles-total" />
        </div>

        <section className="bg-gradient-to-r from-indigo-500/10 to-fuchsia-500/10 border border-indigo-400/20 rounded-2xl p-6">
          <div className="flex items-center gap-2 font-semibold"><Sparkles className="w-4 h-4 text-indigo-400" /> Generate an AI SEO Article</div>
          <p className="text-xs text-slate-400 mt-1.5">AI writes a Google-ready article about your brand with store links. Review the draft, then publish.</p>
          <div className="flex gap-3 mt-4 flex-col sm:flex-row">
            <input
              data-testid="portal-article-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={`e.g. "Why ${brand.name} is the best ${brand.category?.toLowerCase() || "D2C"} pick for 2026"`}
              className="flex-1 bg-white/[0.05] border border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-slate-600 focus:outline-none focus:border-indigo-400/60"
            />
            <button
              data-testid="portal-generate-article"
              onClick={generate}
              disabled={genBusy}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
              style={{ color: "#FFFFFF" }}
            >
              {genBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              {genBusy ? "Writing… ~60s" : "Generate Article"}
            </button>
          </div>
        </section>

        <section>
          <h2 className="font-semibold text-base mb-4">Your Articles</h2>
          {articles.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-white/10 rounded-2xl p-10 text-center" data-testid="portal-no-articles">
              No articles yet — generate your first AI article above.
            </div>
          ) : (
            <div className="space-y-3">
              {articles.map((a) => (
                <div key={a.id} data-testid={`portal-article-${a.id}`} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{a.title}</div>
                    <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${a.status === "published" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                        {a.status}
                      </span>
                      <span>{a.views || 0} views</span>
                      <span>{(a.published_at || a.created_at || "").slice(0, 10)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-300 hover:text-white cursor-pointer flex items-center gap-1.5" data-testid={`add-image-${a.id}`}>
                      <ImagePlus className="w-3 h-3 text-indigo-400" /> Add Image
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onArticleImage(e, a.id)} />
                    </label>
                    {a.status === "published" ? (
                      <>
                        <button onClick={() => setShareArt(a)} data-testid={`share-${a.id}`}
                          className="text-xs px-3 py-1.5 rounded-full bg-indigo-500/15 border border-indigo-400/30 text-indigo-300 hover:text-white flex items-center gap-1.5">
                          <Share2 className="w-3 h-3" /> Share Kit
                        </button>
                        <a href={`/brands/${brand.slug}/${a.slug}`} target="_blank" rel="noopener"
                          className="text-xs px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 hover:border-indigo-400/50">View Live</a>
                        <button onClick={() => setStatus(a.id, "draft")} data-testid={`unpublish-${a.id}`}
                          className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:text-white">Unpublish</button>
                      </>
                    ) : (
                      <button onClick={() => setStatus(a.id, "published")} data-testid={`publish-${a.id}`}
                        className="text-xs px-4 py-1.5 rounded-full bg-emerald-500 font-semibold flex items-center gap-1.5" style={{ color: "#FFFFFF" }}>
                        <Send className="w-3 h-3" /> Publish
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="portal-leads">
          <h2 className="font-semibold text-base mb-4 flex items-center gap-2">
            <Inbox className="w-4 h-4 text-indigo-400" /> Customer Enquiries
            {leads.length > 0 && <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-[11px] font-bold">{leads.length}</span>}
          </h2>
          {leads.length === 0 ? (
            <div className="text-sm text-slate-500 border border-dashed border-white/10 rounded-2xl p-8 text-center" data-testid="portal-no-leads">
              No enquiries yet — visitors on your public page can leave their number &amp; email, and they'll appear here (plus land in your email).
            </div>
          ) : (
            <div className="space-y-2.5">
              {leads.map((l) => (
                <div key={l.id} className="bg-white/[0.03] border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`portal-lead-${l.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-semibold">{l.name || "Visitor"} · <a href={`tel:${l.phone}`} className="text-indigo-300">{l.phone}</a></div>
                    <div className="text-xs text-slate-400 mt-1">{l.email && <span className="mr-3">{l.email}</span>}{l.note}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="portal-growth-toolkit">
          <h2 className="font-semibold text-base mb-4">Free Growth Toolkit</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-2 font-semibold text-sm"><QrCode className="w-4 h-4 text-indigo-400" /> Your Brand QR Code</div>
              <p className="text-xs text-slate-400 mt-1.5">Print it on packaging, visiting cards & posters — customers scan it and land directly on your verified page.</p>
              <div className="flex items-center gap-4 mt-4">
                <img src={`/api/public/brands/${brand.slug}/qr.png`} alt="Brand page QR code"
                  className="w-24 h-24 rounded-lg bg-white p-1.5" data-testid="portal-qr-image" />
                <a href={`/api/public/brands/${brand.slug}/qr.png`} download={`${brand.slug}-brandsbyindia-qr.png`}
                  data-testid="portal-qr-download"
                  className="text-xs px-4 py-2 rounded-full bg-indigo-500 font-semibold" style={{ color: "#FFFFFF" }}>
                  Download QR
                </a>
              </div>
            </div>
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6" data-testid="portal-gbp-guide">
              <div className="flex items-center gap-2 font-semibold text-sm"><MapPin className="w-4 h-4 text-emerald-400" /> Google Business Profile (Free Boost)</div>
              <p className="text-xs text-slate-400 mt-1.5">A free GBP makes your brand show on Google Maps & local searches. 10-minute setup:</p>
              <ol className="mt-3 space-y-1.5 text-xs text-slate-300 list-decimal pl-4">
                <li>Go to <a href="https://business.google.com" target="_blank" rel="noopener" className="text-indigo-400 underline">business.google.com</a> & sign in</li>
                <li>Add your brand name, category & city</li>
                <li>Add your website — use your BrandsByIndia page link</li>
                <li>Upload your logo & 3-5 product photos</li>
                <li>Ask 3 happy customers for a Google review</li>
              </ol>
            </div>
          </div>
        </section>

        <section className="bg-white/[0.03] border border-white/10 rounded-2xl p-6">
          <h2 className="font-semibold text-base mb-4">Brand Profile</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["tagline", "Tagline"],
              ["city", "City"],
              ["website", "Website URL"],
              ["instagram", "Instagram URL"],
              ["myntra_url", "Myntra Store URL"],
              ["meesho_url", "Meesho Store URL"],
              ["logo_url", "Logo Image URL"],
              ["keywords", "Target Keywords (comma separated)"],
            ].map(([k, label]) => (
              <div key={k} className={k === "logo_url" ? "flex items-end gap-2" : ""}>
                <div className="flex-1">
                  <label className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">{label}</label>
                  <input data-testid={`portal-profile-${k}`} value={profile[k] || ""} onChange={(e) => setProfile({ ...profile, [k]: e.target.value })} className={inputCls} />
                </div>
                {k === "logo_url" && (
                  <label className="px-3 py-2 rounded-lg border border-white/10 text-xs text-slate-300 hover:text-white cursor-pointer flex items-center gap-1.5 whitespace-nowrap" data-testid="portal-upload-logo">
                    <ImagePlus className="w-3.5 h-3.5 text-indigo-400" /> Upload
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoFile} />
                  </label>
                )}
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">About the Brand</label>
              <textarea data-testid="portal-profile-description" rows={4} value={profile.description || ""} onChange={(e) => setProfile({ ...profile, description: e.target.value })} className={inputCls} />
            </div>
          </div>
          <button
            data-testid="portal-save-profile"
            onClick={saveProfile}
            disabled={saveBusy}
            className="mt-5 px-6 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
            style={{ color: "#FFFFFF" }}
          >
            <Save className="w-4 h-4" /> {saveBusy ? "Saving…" : "Save Profile"}
          </button>
        </section>
      </main>

      {shareArt && (() => {
        const url = `${window.location.origin}/brands/${brand.slug}/${shareArt.slug}`;
        const tag = brand.name.replace(/[^a-zA-Z0-9]/g, "");
        const kits = [
          { label: "WhatsApp", text: `*${shareArt.title}*\n\n${shareArt.meta_description || ""}\n\nRead here 👉 ${url}\n\n_${brand.name} · Verified on BrandsByIndia_` },
          { label: "Instagram Caption", text: `${shareArt.title} ✨\n\n${shareArt.meta_description || ""}\n\nFull guide — link in bio 👉 ${url}\n\n#${tag} #BrandsByIndia #MadeInIndia #${(brand.category || "D2C").replace(/[^a-zA-Z0-9]/g, "")} #IndianBrands`, },
          { label: "LinkedIn Post", text: `${shareArt.title}\n\n${shareArt.meta_description || ""}\n\nProud moment for ${brand.name} — our brand is verified on BrandsByIndia with a growing library of guides.\n\nRead: ${url}\n\n#${tag} #BrandsByIndia #MakeInIndia` },
        ];
        const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied — paste & post!"); };
        return (
          <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShareArt(null)} data-testid="share-kit-modal">
            <div className="w-full max-w-lg bg-[#0B0F1E] border border-white/10 rounded-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-white font-semibold flex items-center gap-2"><Share2 className="w-4 h-4 text-indigo-400" /> Share Kit</div>
                <button onClick={() => setShareArt(null)} className="text-slate-500 hover:text-white" data-testid="share-kit-close"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-xs text-slate-400 mb-4">Ready-to-post captions — sharing drives real traffic & Google signals to your brand.</p>
              <div className="space-y-4">
                {kits.map((k) => (
                  <div key={k.label} className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[11px] uppercase tracking-wider text-indigo-400 font-bold">{k.label}</div>
                      <button onClick={() => copy(k.text)} data-testid={`copy-${k.label.split(" ")[0].toLowerCase()}`}
                        className="text-xs px-3 py-1 rounded-full border border-white/10 text-slate-300 hover:text-white flex items-center gap-1.5">
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <pre className="text-[11px] text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{k.text}</pre>
                  </div>
                ))}
              </div>
              <a href={`https://wa.me/?text=${encodeURIComponent(kits[0].text)}`} target="_blank" rel="noopener"
                data-testid="share-whatsapp-direct"
                className="mt-4 w-full py-3 rounded-xl bg-emerald-500 text-sm font-semibold flex items-center justify-center gap-2" style={{ color: "#FFFFFF" }}>
                <Send className="w-4 h-4" /> Share on WhatsApp Now
              </a>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
